/**
 * usePlayerSalesVisibility Hook
 * Central hook to determine if Player Sales should be visible
 *
 * Checks three conditions:
 * 1. Organization setting (displayPlayerSales)
 * 2. Plan feature (playerSalesTracking, from DB entitlements)
 * 3. User permission (canViewTransfer)
 */

import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';

export function usePlayerSalesVisibility() {
  const { organization } = useOrganization();
  const { canAccessFeature } = useEntitlements();
  const { data: permissionsData } = usePermissionsQuery();

  // Check organization setting (defaults to true if not set)
  const organizationEnabled =
    (organization?.label_aliases as Record<string, string>)?.display_player_sales !== 'false';

  // Check plan feature
  const planEnabled = canAccessFeature('playerSalesTracking');

  // Check user permissions (view and edit are separate)
  const userCanView = permissionsData?.canViewTransfer ?? false;
  const userCanEdit = permissionsData?.canEditTransfer ?? false;

  // Player Sales is visible if organization enabled AND plan enabled AND user can view
  const isVisible = organizationEnabled && planEnabled && userCanView;

  // User can edit if it's visible AND they have edit permission
  const canEdit = isVisible && userCanEdit;

  return {
    isVisible,
    canEdit,
    organizationEnabled,
    planEnabled,
    userCanView,
    userCanEdit,
  };
}
